export type Field = { name: string; label: string; type: string; required?: boolean; options?: string[]; placeholder?: string }
export type FormSchema = { fields: Field[] }
// Matches server/catalog.ts listArtefacts() — summaries only, no formSchema/intro/etc.
export type ArtefactSummary = {
  id: string; type: string; build_pattern: string; department: string; title: string
  topic_id: string | null; review_status: string
  tags: { dimension: string; value: string }[]
}
export type ArtefactDetail = ArtefactSummary & {
  breadcrumb: string | null; intro: string | null
  formSchema: FormSchema; sidebarModule: string | null
}
// Matches server/topics.ts
export type TopicSummary = {
  id: string; title: string; department: string; quality_area: string
  summary: string | null; review_status: string
}
export type TopicArtefact = {
  id: string; title: string; type: string; review_status: string; sort_order: number
}
export type TopicDetail = TopicSummary & { artefacts: TopicArtefact[] }
// Matches server/doc-control.ts
export type DocControlEntry = {
  version: string; dateReviewed: string; modifications: string
  nextReviewDate: string; roleResponsible: string
}
// Matches server/render.ts
export type RenderedDocument = {
  id: string; title: string; type: string
  markdown: string; disclaimer: string; reviewStatus: string
  // Optional on the client: /api/render always sends them, but the knowledge
  // test and meeting guide reuse RenderedDoc without any document control.
  docControl?: DocControlEntry[]
  relatedDocs?: string[]
}
// Matches server/knowledge-test.ts
export type KnowledgeTestResult = {
  title: string; markdown: string; answerKeyMarkdown: string
  disclaimer: string; reviewStatus: string
}
// Matches server/meeting-guide.ts
export type MeetingGuideResult = {
  title: string; markdown: string; aiQuestions: string | null
  disclaimer: string; reviewStatus: string
}
// Matches server/bundle.ts
export type BundleDocument = {
  id: string; title: string; filename: string; markdown: string; reviewStatus: string
}
export type BundleResult = {
  topic: { id: string; title: string; reviewStatus: string }
  anyDraft: boolean
  documents: BundleDocument[]
  disclaimer: string
}
export type Sidebar = { title: string; items: { label: string; due: string }[] } | null
export type GenerateResult = {
  document: string; disclaimer: string
  sidebar: Sidebar
}

/** Callbacks for the streamed /api/generate response (see server/generate.ts). */
export type StreamHandlers = {
  onMeta: (m: { sidebar: Sidebar; disclaimer: string }) => void
  onDelta: (text: string) => void
  onDone: () => void
  onError: (error: string) => void
}

async function json<T>(res: Response): Promise<T> {
  if (!res.ok) throw new Error(String(res.status))
  return res.json() as Promise<T>
}

export const api = {
  session: () => fetch('/api/auth/session').then((r) => r.ok),
  login: (password: string) =>
    fetch('/api/auth/login', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ password }) }).then((r) => r.ok),
  catalog: () => fetch('/api/catalog').then((r) => json<ArtefactSummary[]>(r)),
  tool: (id: string) => fetch(`/api/tools/${id}`).then((r) => json<ArtefactDetail>(r)),
  topics: () => fetch('/api/topics').then((r) => json<TopicSummary[]>(r)),
  topic: (id: string) => fetch(`/api/topics/${id}`).then((r) => json<TopicDetail>(r)),
  render: (artefactId: string, brandKit: Record<string, string>) =>
    fetch(`/api/render/${artefactId}`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ brandKit }),
    }).then((r) => json<RenderedDocument>(r)),
  knowledgeTest: (topicId: string, brandKit: Record<string, string>) =>
    fetch(`/api/topics/${topicId}/knowledge-test`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ brandKit }),
    }).then((r) => json<KnowledgeTestResult>(r)),
  meetingGuide: (topicId: string, brandKit: Record<string, string>, includeDiscussionQuestions = false) =>
    fetch(`/api/topics/${topicId}/meeting-guide`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ brandKit, includeDiscussionQuestions }),
    }).then((r) => json<MeetingGuideResult>(r)),
  bundle: (topicId: string, brandKit: Record<string, string>) =>
    fetch(`/api/topics/${topicId}/bundle`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ brandKit }),
    }).then((r) => json<BundleResult>(r)),
  /**
   * Streams /api/generate. Rejects on the pre-stream HTTP errors (401/404/429);
   * a mid-stream failure arrives as onError instead. Resolves when the stream ends.
   */
  generateStream: async (toolId: string, formData: Record<string, unknown>, h: StreamHandlers) => {
    const res = await fetch('/api/generate', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ toolId, formData }),
    })
    if (!res.ok || !res.body) throw new Error(String(res.status))

    const reader = res.body.getReader()
    const decoder = new TextDecoder()
    let buf = ''
    for (;;) {
      const { done, value } = await reader.read()
      if (done) break
      buf += decoder.decode(value, { stream: true })
      let nl: number
      while ((nl = buf.indexOf('\n')) >= 0) {
        const line = buf.slice(0, nl).trim()
        buf = buf.slice(nl + 1)
        if (!line) continue
        const msg = JSON.parse(line) as
          | { type: 'meta'; sidebar: Sidebar; disclaimer: string }
          | { type: 'delta'; text: string }
          | { type: 'done' }
          | { type: 'error'; error: string }
        if (msg.type === 'delta') h.onDelta(msg.text)
        else if (msg.type === 'meta') h.onMeta({ sidebar: msg.sidebar, disclaimer: msg.disclaimer })
        else if (msg.type === 'error') h.onError(msg.error)
        else if (msg.type === 'done') h.onDone()
      }
    }
  },
}
